import { useTranslation } from 'react-i18next'
import type { JTLDescription } from '@/lib/api/descriptions'
import { CONTENT_FIELDS, FIELD_LABELS, MULTILINE_FIELDS, LANGUAGE_LABELS } from './channel-constants'

interface ChannelTranslateColumnProps {
  description: JTLDescription | undefined
  sourceLang: string
}

const SOURCE_CLS =
  'w-full rounded-md px-3 py-2 text-sm resize-none bg-neutral-100 dark:bg-[rgba(38,38,38,0.5)] border border-neutral-200 dark:border-line text-neutral-500 dark:text-neutral-400 cursor-default focus:outline-none'

const SEO_FIELDS = ['SeoTitleTag', 'SeoMetaDescription', 'SeoMetaKeywords', 'SeoPath'] as const

export function ChannelTranslateColumn({ description, sourceLang }: ChannelTranslateColumnProps) {
  const { t } = useTranslation()
  const langLabel = LANGUAGE_LABELS[sourceLang] ?? sourceLang.toUpperCase()

  const renderField = (key: (typeof CONTENT_FIELDS)[number]) => {
    const value = description?.[key] ?? ''
    const isMultiline = MULTILINE_FIELDS.has(key)
    const rows = key === 'Description' ? 8 : 2
    return (
      <div key={key}>
        <label className="block text-2xs font-medium text-neutral-600 uppercase tracking-wider mb-1">
          {t(FIELD_LABELS[key])}
        </label>
        {isMultiline ? (
          <textarea rows={rows} readOnly value={value} className={SOURCE_CLS} />
        ) : (
          <input type="text" readOnly value={value} className={SOURCE_CLS} />
        )}
      </div>
    )
  }

  return (
    <div className="border-r border-neutral-200 dark:border-neutral-800 flex flex-col">
      <div className="sticky top-0 z-[5] px-4 py-2 border-b border-neutral-200 dark:border-neutral-800 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm">
        <div className="flex items-center gap-1.5">
          <span className="text-2xs font-medium text-neutral-400 uppercase tracking-wider">
            {t('productContent.channels.source')}
          </span>
          <span className="text-2xs px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 font-medium">
            {langLabel}
          </span>
          <span className="text-2xs text-neutral-600">· {t('productContent.channels.readOnly')}</span>
        </div>
      </div>
      <div className="px-4 py-4 space-y-4 overflow-y-auto flex-1">
        {renderField('Name')}
        {renderField('ShortDescription')}
        {renderField('Description')}
        <div className="border-t border-neutral-200 dark:border-neutral-800 pt-3">
          <div className="text-2xs font-medium text-neutral-600 uppercase tracking-wider mb-2">
            {t('productContent.channels.seoFields')}
          </div>
          <div className="space-y-2">
            {SEO_FIELDS.map(renderField)}
          </div>
        </div>
      </div>
    </div>
  )
}
